import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  ledgersApi,
  type LedgerStatus,
  type LoanActivityLedgerRow,
} from "@/api/ledgers";
import { Badge, Button, Card, Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  downloadCsv,
  formatDate,
  formatMoney,
} from "./ledgerHelpers";

const STATUS_OPTIONS: (LedgerStatus | "")[] = [
  "",
  "Live",
  "Instructed",
  "Reversed",
  "Adjusted",
];

function statusBadge(status: LedgerStatus) {
  const map: Record<LedgerStatus, "draft" | "info" | "review" | "finalized" | "error"> = {
    Live: "info",
    Instructed: "finalized",
    Reversed: "error",
    Adjusted: "review",
  };
  return <Badge status={map[status]}>{status}</Badge>;
}

/**
 * Payment System v3.0 PR 11 — Loan Activity Ledger (design spec §10.7).
 * Reads /api/ledgers/loans/{loanId}/activity.
 */
export function LoanActivityLedgerPage() {
  const { loanId } = useParams<{ loanId: string }>();
  const [status, setStatus] = useState<LedgerStatus | "">("");
  const [paymentType, setPaymentType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const query = useQuery({
    queryKey: ["ledger.loan", loanId, status, paymentType, from, to],
    queryFn: () =>
      ledgersApi.loanActivity(loanId!, {
        status: status || undefined,
        paymentType: paymentType || undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: !!loanId,
  });

  const columns: Column<LoanActivityLedgerRow>[] = [
    { key: "date", header: "Date", render: (r) => formatDate(r.date) },
    { key: "paymentType", header: "Payment Type", render: (r) => r.paymentType },
    {
      key: "wf",
      header: "Waterfall #",
      render: (r) => `WF${r.waterfallNumber}`,
    },
    { key: "priority", header: "Priority", render: (r) => r.priorityPosition },
    {
      key: "obligation",
      header: "Obligation Type",
      render: (r) => r.obligationTypeDisplay ?? r.obligationTypeCode,
    },
    {
      key: "recipient",
      header: "Recipient",
      render: (r) => r.recipient ?? "—",
    },
    {
      key: "amount",
      header: "Amount",
      render: (r) => formatMoney(r.amount),
    },
    {
      key: "balance",
      header: "Balance After",
      render: (r) => formatMoney(r.balanceAfter),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => statusBadge(r.status),
    },
  ];

  const data = query.data;

  function handleExportCsv() {
    if (!data) return;
    downloadCsv(
      `loan-activity-${data.loanNumber ?? data.loanId}.csv`,
      [
        "Date",
        "Payment Type",
        "Waterfall #",
        "Priority",
        "Obligation Type Code",
        "Obligation Type",
        "Recipient",
        "Amount",
        "Balance After",
        "Status",
      ],
      data.rows.map((r) => [
        r.date,
        r.paymentType,
        r.waterfallNumber,
        r.priorityPosition,
        r.obligationTypeCode,
        r.obligationTypeDisplay ?? "",
        r.recipient ?? "",
        r.amount,
        r.balanceAfter ?? "",
        r.status,
      ]),
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Ledger"
        title={`Loan Activity — ${data?.loanNumber ?? loanId}`}
        description="Append-only ledger of every disbursement against this loan. Filter by status, payment type, or date range; export the visible rows as CSV."
        actions={
          <Button onClick={handleExportCsv} disabled={!data?.rows.length}>
            <Download size={16} aria-hidden /> Export CSV
          </Button>
        }
      />

      <Card className="mb-space-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 mb-space-3">
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as LedgerStatus | "")}
              className="rounded-md border border-border-light p-space-2 bg-white-pure"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s || "All"}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">Payment Type</span>
            <Input
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              placeholder="e.g. monthly_p_and_i"
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">From</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">To</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
        </div>

        {data && (
          <div className="grid grid-cols-3 gap-space-3 text-small">
            <Totals label="Total Live" value={data.totalLive} />
            <Totals label="Total Reversed" value={data.totalReversed} />
            <Totals label="Total Adjusted" value={data.totalAdjusted} />
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        loading={query.isLoading}
        error={query.isError ? (query.error as Error).message : null}
        emptyMessage="No activity for the selected filters."
        rowKey={(r) => r.disbursementId}
      />
    </>
  );
}

function Totals({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border-light p-space-3 bg-white-warm">
      <div className="text-caption uppercase text-gray-mid">{label}</div>
      <div className="text-body font-semibold">{formatMoney(value)}</div>
    </div>
  );
}
